import { Link } from "react-router-dom";
import type { AnimeItem } from "@/mocks/anime";

interface AnimeCardProps {
  anime: AnimeItem;
  layout?: "grid" | "list";
}

export default function AnimeCard({ anime, layout = "grid" }: AnimeCardProps) {
  if (layout === "list") {
    return (
      <Link
        to={`/anime/${anime.id}`}
        className="group flex gap-4 p-3 md:p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="relative w-24 h-32 md:w-28 md:h-36 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={anime.cover}
            alt={`${anime.title} 封面`}
            title={`${anime.title} - 动漫作品`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <h3 className="font-heading font-semibold text-foreground-950 text-base truncate group-hover:text-primary-600 transition-colors">
            {anime.title}
          </h3>
          <p className="mt-1.5 text-xs text-foreground-600 line-clamp-2 leading-relaxed">{anime.synopsis}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {anime.genre.slice(0, 3).map((g) => (
              <span key={g} className="px-2 py-0.5 rounded-full text-[11px] bg-secondary-100 text-secondary-900">
                {g}
              </span>
            ))}
          </div>
          <div className="mt-auto pt-2 flex items-center gap-3 text-xs text-foreground-600">
            <span className="inline-flex items-center gap-1 text-primary-600 font-semibold">
              <i className="ri-star-fill" />
              {anime.rating.toFixed(1)}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-map-pin-line" />
              {anime.region}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-tv-2-line" />
              {anime.status}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/anime/${anime.id}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="relative w-full aspect-[3/4] overflow-hidden bg-background-200">
        <img
          src={anime.cover}
          alt={`${anime.title} 封面`}
          title={`${anime.title} - 动漫作品`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md text-[11px] font-medium bg-primary-500 text-background-50">
          {anime.status}
        </span>
        <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-foreground-950/70 text-background-50">
          <i className="ri-star-fill text-primary-400" />
          {anime.rating.toFixed(1)}
        </span>
      </div>
      <div className="p-3 md:p-4 flex flex-col flex-1">
        <h3 className="font-heading font-semibold text-foreground-950 text-sm md:text-base truncate group-hover:text-primary-600 transition-colors">
          {anime.title}
        </h3>
        <p className="mt-1 text-xs text-foreground-600 line-clamp-2 leading-relaxed">{anime.synopsis}</p>
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {anime.genre.slice(0, 2).map((g) => (
            <span key={g} className="px-2 py-0.5 rounded-full text-[11px] bg-secondary-100 text-secondary-900">
              {g}
            </span>
          ))}
          <span className="px-2 py-0.5 rounded-full text-[11px] bg-accent-100 text-accent-800">{anime.year}</span>
        </div>
      </div>
    </Link>
  );
}